import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { API_CONFIG } from '@/config/envConfig.ts';
import { macOSNotificationManager } from '@/utils/macOSNotificationManager.ts';

interface GraphQLMessage {
  type: string;
  payload?: any;
  id?: string;
}

// Global notification state (singleton for cross-navigation persistence)
let globalNotificationState = {
  isConnected: false,
  isLoading: true,
  notificationCount: 0,
  notifications: [] as any[],
};

// Global WebSocket instance and cleanup
let globalWs: WebSocket | null = null;
let reconnectTimeout: NodeJS.Timeout | null = null;
let connectionTimeout: NodeJS.Timeout | null = null;
let isInitialized = false;

// State change listeners for UI updates with previous state tracking
const listeners = new Set<(state: typeof globalNotificationState) => void>();
let previousState = { ...globalNotificationState };

const notifyListeners = () => {
  // Only notify if state actually changed
  const currentState = globalNotificationState;
  if (
    currentState.isConnected !== previousState.isConnected ||
    currentState.isLoading !== previousState.isLoading ||
    currentState.notificationCount !== previousState.notificationCount ||
    currentState.notifications.length !== previousState.notifications.length
  ) {
    previousState = { ...currentState };
    listeners.forEach(fn => fn(currentState));
  }
};

// Global cleanup function
const globalCleanup = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  if (connectionTimeout) {
    clearTimeout(connectionTimeout);
    connectionTimeout = null;
  }
  if (globalWs) {
    globalWs.close();
    globalWs = null;
  }

  // Reset global state
  globalNotificationState = {
    isConnected: false,
    isLoading: true,
    notificationCount: 0,
    notifications: [],
  };

  isInitialized = false;
  previousState = { ...globalNotificationState };
  notifyListeners();
};

// Start notification subscription
const startNotificationSubscription = (ws: WebSocket, userId: string) => {
  const subscriptionQuery = {
    id: "notification-subscription",
    type: "start",
    payload: {
      query: `subscription MyQuery($assignedCSId: Int!) {
              pep_vw_patient_user_transfer(
                where: {
                  _or: [
                    {assigned_to_user: {_is_null: true}},
                    {assigned_to_user: {_eq: $assignedCSId}}
                  ]
                },
                order_by: {assigned_dt: desc}
              ) {
                transfer_id
                enrollment_patient_id
                patient_name
                clinic_id
                clinic_name
                assigned_by_user_id
                assigned_by_user_name
                assigned_to_user
                assigned_to_user_name
                assigned_dt
              }
            }`,
      variables: {
        assignedCSId: parseInt(userId)
      }
    }
  };

  ws.send(JSON.stringify(subscriptionQuery));
};

// Handle notification data
const handleNotificationData = (data: any) => {
  if (data && data.pep_vw_patient_user_transfer) {
    const newNotifications = data.pep_vw_patient_user_transfer;
    console.log('🔔 Received notification data:', { 
      totalCount: newNotifications.length,
      notifications: newNotifications 
    });

    // Filter notifications to only count records where assigned_to_user is null
    const unassignedNotifications = newNotifications.filter((notification: any) => notification.assigned_to_user === null);

    // Check if there are new unassigned patients compared to previous state
    const previousCount = globalNotificationState.notificationCount;
    const currentCount = unassignedNotifications.length;

    console.log('🔔 Notification counts:', { 
      previousCount, 
      currentCount, 
      unassignedCount: unassignedNotifications.length 
    });

    globalNotificationState.notifications = newNotifications;
    globalNotificationState.notificationCount = currentCount;
    globalNotificationState.isLoading = false;

    // Only trigger notification or toast if there are NEW unassigned patients (not on initial load)
    // Allow notifications when count increases from 0 to positive, but prevent on initial data load
    if (currentCount > previousCount && currentCount > 0 && previousCount >= 0) {
      const newPatientCount = currentCount - previousCount;
      console.log(`New unassigned patients detected: ${newPatientCount}`);

      // Check if notification permission is granted
      if (Notification.permission === 'granted') {
        const notificationTitle = newPatientCount === 1
          ? 'New Patient Transfer'
          : `${newPatientCount} New Patient Transfers`;
        const notificationBody = newPatientCount === 1
          ? 'A new patient has been transferred and needs assignment.'
          : `${newPatientCount} new patients have been transferred and need assignment.`;

        new Notification(notificationTitle, {
          body: notificationBody,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'patient-transfer',
          requireInteraction: true
        });
        console.log('Browser notification sent');
      } else {
        // Fallback: Show macOS-style notification when browser notifications are disabled
        console.log('Browser notifications not enabled, showing fallback macOS-style notification');

        const notificationTitle = newPatientCount === 1
          ? 'New Patient Transfer'
          : `${newPatientCount} New Patient Transfers`;
        const notificationBody = newPatientCount === 1
          ? 'A new patient has been transferred and needs assignment.'
          : `${newPatientCount} new patients have been transferred and need assignment.`;

        // Use the global macOS notification manager
        try {
          macOSNotificationManager.addNotification(notificationTitle, notificationBody);
        } catch (error) {
          console.error('Failed to show macOS notification:', error);
        }
      }
    }

    notifyListeners();
  }
};

// Connect WebSocket
const connectGlobalWebSocket = (hasuraToken: string, userId: string) => {
  if (globalWs) {
    return;
  }
  globalNotificationState.isLoading = true;
  notifyListeners();

  const ws = new WebSocket(API_CONFIG.GRAPHQL_WEBSOCKET_URL, ['graphql-transport-ws']);
  globalWs = ws;

  ws.onopen = () => {
    // Set connection timeout
    connectionTimeout = setTimeout(() => {
      ws.close();
    }, 10000);

    // Send connection init message
    const initMessage = {
      type: 'connection_init',
      payload: {
        headers: {
          'Authorization': `Bearer ${hasuraToken}`,
          'X-Hasura-Role': 'user',
          'Content-Type': 'application/json'
        }
      }
    };
    ws.send(JSON.stringify(initMessage));
  };

  ws.onmessage = (event) => {
    try {
      const message: GraphQLMessage = JSON.parse(event.data);

      switch (message.type) {
        case 'connection_ack':
          globalNotificationState.isConnected = true;
          globalNotificationState.isLoading = false;

          if (connectionTimeout) {
            clearTimeout(connectionTimeout);
            connectionTimeout = null;
          }

          startNotificationSubscription(ws, userId);
          notifyListeners();
          break;

        case 'ping':
          ws.send(JSON.stringify({ type: 'pong' }));
          break;

        case 'next':
          if (message.payload && message.payload.data && message.id === 'notification-subscription') {
            handleNotificationData(message.payload.data);
          }
          break;

        case 'error':
        case 'connection_error':
          console.error('Global notification WebSocket error:', message.payload);
          globalNotificationState.isLoading = false;
          globalNotificationState.isConnected = false;
          notifyListeners();
          break;

        case 'complete':
          globalNotificationState.isLoading = false;
          notifyListeners();
          break;
      }
    } catch (error) {
      console.error('Error parsing global notification WebSocket message:', error);
    }
  };

  ws.onclose = (event) => {
    globalNotificationState.isConnected = false;
    globalNotificationState.isLoading = false;
    globalWs = null;
    notifyListeners();

    // Only attempt reconnection if it wasn't a manual close
    if (event.code !== 1000 && hasuraToken && userId) {
      reconnectTimeout = setTimeout(() => {
        connectGlobalWebSocket(hasuraToken, userId);
      }, 3000);
    }
  };

  ws.onerror = (error) => {
    console.error('Global notification WebSocket error:', error);
    globalNotificationState.isConnected = false;
    globalNotificationState.isLoading = false;
    notifyListeners();
  };
};

// Main hook
export const useGlobalNotifications = () => {
  const { hasuraToken, user } = useAuth();
  const [, forceUpdate] = useState(0);
  const wasLoggedIn = useRef(false);

  // Subscribe to state changes with memoization
  useEffect(() => {
    const listener = (newState: typeof globalNotificationState) => {
      // Only force update if this component's relevant state changed
      forceUpdate(prev => prev + 1);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Initialize notifications on mount (once per session)
  useEffect(() => {
    // Only initialize for CS (Care Specialist), CA (CS Admin), and ED (Enrollment Director) users
    // VPE users (ES role) should not receive global transfer notifications
    if (!user || !hasuraToken || !['CS', 'CA', 'ED'].includes(user.type)) {
      console.log('🔔 Global Notifications - Skipping initialization:', { 
        hasUser: !!user, 
        hasToken: !!hasuraToken, 
        userType: user?.type 
      });
      return;
    }

    console.log('🔔 Global Notifications - Initializing for user:', { 
      userId: user.id, 
      userType: user.type,
      isInitialized 
    });

    // Initialize only once per session
    if (!isInitialized) {
      isInitialized = true;
      connectGlobalWebSocket(hasuraToken, user.id);
    }
  }, [user, hasuraToken]);

  // Handle logout cleanup
  useEffect(() => {
    if (user && hasuraToken) {
      wasLoggedIn.current = true;
    } else if (wasLoggedIn.current) {
      // User logged out - cleanup
      globalCleanup();
      wasLoggedIn.current = false;
    }
  }, [user, hasuraToken]);

  // Component cleanup on unmount - only for logout scenarios
  useEffect(() => {
    return () => {
      if (!user && !hasuraToken) {
        globalCleanup();
      }
    };
  }, [user, hasuraToken]);

  const refetch = useCallback(() => {
    // Don't cleanup and reconnect if already connected - this prevents duplicate connections
    if (globalNotificationState.isConnected && globalWs) {
      return;
    }
    
    globalCleanup();
    // Only refetch for CS, CA, and ED users, not VPE users (ES role)
    if (hasuraToken && user && ['CS', 'CA', 'ED'].includes(user.type)) {
      setTimeout(() => {
        connectGlobalWebSocket(hasuraToken, user.id);
      }, 100);
    }
  }, [hasuraToken, user]);

  return {
    isConnected: globalNotificationState.isConnected,
    isLoading: globalNotificationState.isLoading,
    notificationCount: globalNotificationState.notificationCount,
    notifications: globalNotificationState.notifications,
    refetch
  };
};

// Export global cleanup function for external use
export const cleanupGlobalNotifications = globalCleanup;